/**
 * Deposit Location Selection Page - Cash-in location selection
 *
 * Allows user to find and select nearby locations that support cash-in.
 * Features Map view (default) and List view toggle.
 * Validates coordinates against selected country and uses defaults when mismatched.
 * Calls quote API with stagingInfo when user continues to capture deposit fees.
 */

import { t } from "@lingui/core/macro";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Check,
	Clock,
	List,
	Map as MapIcon,
	MapPin,
	Navigation,
	Search,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { PickupLocation, QuoteRequest } from "@/api";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useContinue } from "@/hooks/useContinue";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useNearbyLocations } from "@/hooks/useNearbyLocations";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";
import { useAnalytics } from "@/hooks/usePostHog";
import type { CashInLocation } from "../api/types/moneygram";
import { RampsButton } from "../components/ramps/Button";
import { MapComponent } from "../components/ramps/MapComponent";
import { RampsText } from "../components/ramps/Text";
import { LoadingSpinner } from "../components/ui/loading-spinner";
import {
	getAuthTokenFromSearchParams,
	validateRampsSearch,
} from "../lib/ramps-search-schema";
import { toast } from "../lib/toast";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";
import { getSearchCoordinates } from "../utils/coordinate-country-validation";
import { ensureISO2Format } from "../utils/country-utils";
import { getPlaceDetails } from "../utils/google-places-autocomplete";
import {
	getClosingTime,
	isLocation24Hours,
	isLocationOpen,
} from "../utils/location-hours";

// View modes
type ViewMode = "map" | "list";

function DepositLocationPage() {
	const search = Route.useSearch();
	const continueToNextPage = useContinue();
	const store = useRampsStore();
	const { trackPageView, trackEvent } = useAnalytics();

	// UI state
	const [viewMode, setViewMode] = useState<ViewMode>("map");
	const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
		store.cashInLocation?.locationId || null,
	);
	const [searchedLocation, setSearchedLocation] = useState<{
		lat: number;
		lng: number;
		name: string;
	} | null>(null);
	const [mapCenter, setMapCenter] = useState<{
		lat: number;
		lng: number;
	} | null>(null);

	// Initialize API client for quote request
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Track page view
	useEffect(() => {
		trackPageView("deposit-location", {
			page_name: "deposit-location",
		});
	}, [trackPageView]);

	// Quote mutation for deposit flow - calls quote API with stagingInfo
	const quoteMutation = useMutation({
		mutationFn: async (quoteRequest: QuoteRequest) => {
			return apiClient.quote.getQuote(quoteRequest, {
				params: { operation: "deposit" },
			});
		},
		onSuccess: (quoteResponse) => {
			// Store the quote response
			rampsActions.setQuoteResponse(quoteResponse);

			// Set service option for deposit flow
			rampsActions.setServiceOption("DIRECT_TO_ACCT");

			// If sender profile is available, prefill user data
			// This works for both v2 and v5 API - just check if senderProfile exists
			if (quoteResponse.senderProfile) {
				rampsActions.setSenderProfile(quoteResponse.senderProfile);

				// Track if this is a returning user (v5 API provides isExistingProfile flag)
				if (quoteResponse.isExistingProfile) {
					trackEvent("deposit_returning_user", {
						has_profile: true,
					} as Record<string, unknown>);
				}
			}

			// Use useContinue to navigate - it will skip pages where data is already filled
			// For returning users, this will skip directly to review
			continueToNextPage(search);
		},
		onError: (error) => {
			console.error("Quote error:", error);
			toast.error(t`Failed to get quote. Please try again.`);
		},
	});

	// Get user's geolocation
	const {
		latitude: userLat,
		longitude: userLng,
		loading: geoLoading,
	} = useGeolocation({
		enableHighAccuracy: false,
		timeout: 10000,
		maximumAge: 10 * 60 * 1000,
	});

	// Get the selected country's ISO2 code for API calls
	const countryCode = ensureISO2Format(
		store.payoutCountry,
		store.countries,
		"US",
	);

	// Get default coordinates for the selected country (fallback when no GPS/search)
	const defaultCoords = useMemo(() => {
		return getSearchCoordinates(null, null, countryCode);
	}, [countryCode]);

	// Determine search coordinates - flexible approach:
	// 1. Use searched location if user searched
	// 2. Use current GPS if available
	// 3. Fall back to country defaults
	const searchCoordinates = useMemo(() => {
		// Priority 1: User searched for a specific location
		if (searchedLocation) {
			return {
				lat: searchedLocation.lat,
				lng: searchedLocation.lng,
				source: "search" as const,
			};
		}

		// Priority 2: Use map center if user panned
		if (mapCenter) {
			return {
				lat: mapCenter.lat,
				lng: mapCenter.lng,
				source: "map" as const,
			};
		}

		// Priority 3: Use current GPS if available
		if (userLat && userLng) {
			return {
				lat: userLat,
				lng: userLng,
				source: "gps" as const,
			};
		}

		// Priority 4: Fall back to country defaults
		return {
			lat: defaultCoords.lat,
			lng: defaultCoords.lng,
			source: "default" as const,
		};
	}, [userLat, userLng, searchedLocation, mapCenter, defaultCoords]);

	// Places autocomplete for city/address search
	const {
		inputValue,
		setInputValue,
		suggestions,
		isLoading: isAutocompleteLoading,
		clear: clearAutocomplete,
	} = usePlacesAutocomplete({
		regionCode: countryCode,
		languageCode: "en",
		minInputLength: 2,
		maxSuggestions: 5,
	});

	// Fetch locations based on coordinates
	// Uses selected country for filtering, coordinates for proximity search
	const {
		data: locationsData,
		isLoading,
		error: locationsError,
		refetch,
	} = useNearbyLocations({
		latitude: searchCoordinates.lat,
		longitude: searchCoordinates.lng,
		searchRadius: searchCoordinates.source === "default" ? 50 : 25,
		limit: 30,
		country: countryCode,
		radiusuom: "mile",
		agent: "stellarcashout",
		authToken: store.token || "",
		enabled: !geoLoading,
	});

	const allLocations = locationsData?.locations || [];

	// Filter locations that support cash-in (receiveMoney: true)
	const depositLocations = useMemo(() => {
		return allLocations.filter(
			(location) => location.services?.receiveMoney === true,
		);
	}, [allLocations]);

	// Get selected location object
	const selectedLocation = useMemo(() => {
		return (
			depositLocations.find((loc) => loc.id === selectedLocationId) || null
		);
	}, [depositLocations, selectedLocationId]);

	// Handle place selection from autocomplete
	const handlePlaceSelect = async (
		prediction: Awaited<
			ReturnType<typeof usePlacesAutocomplete>
		>["suggestions"][0],
	) => {
		try {
			const placeDetails = await getPlaceDetails(prediction.place_id);
			if (!placeDetails?.result?.geometry?.location) {
				console.error("Failed to get place coordinates");
				return;
			}

			const { lat, lng } = placeDetails.result.geometry.location;
			const locationName =
				prediction.structured_formatting?.main_text || prediction.description;

			setSearchedLocation({ lat, lng, name: locationName });
			setMapCenter(null); // Reset map center when searching
			clearAutocomplete();

			trackEvent("deposit_location_search", {
				search_term: locationName,
				country: countryCode,
			} as Record<string, unknown>);
		} catch (error) {
			console.error("Error selecting place:", error);
		}
	};

	// Handle "Search this area" button - triggers refetch at current coordinates
	const handleSearchThisArea = useCallback(() => {
		// Refetch locations at current search coordinates
		refetch();
	}, [refetch]);

	// Handle GPS button - center on user location
	const handleCenterOnUser = useCallback(() => {
		if (userLat && userLng) {
			setMapCenter(null);
			setSearchedLocation(null);
		}
	}, [userLat, userLng]);

	// Handle location selection
	const handleLocationSelect = (locationId: string) => {
		setSelectedLocationId(locationId);
		const location = depositLocations.find((loc) => loc.id === locationId);

		if (location) {
			const cashInLocation: CashInLocation = {
				locationId: location.id,
				name: location.name,
				address: location.address,
				city: location.city,
				state: location.state,
				country: location.country,
				formFreeCompletion: location.services?.formFreeCompletion || false,
				latitude: location.latitude.toString(),
				longitude: location.longitude.toString(),
			};

			rampsActions.setCashInLocation(cashInLocation);

			trackEvent("deposit_location_selected", {
				location_id: location.id,
				location_name: location.name,
				has_form_free: cashInLocation.formFreeCompletion,
				view_mode: viewMode,
			} as Record<string, unknown>);
		}
	};

	// Handle continue to next step - calls quote API with stagingInfo
	const handleContinue = async () => {
		if (!store.cashInLocation) return;

		// If coming from review (editing location), just return to review
		// The location is already stored, no need to call quote again
		if (search.from_review === "true") {
			trackEvent("deposit_location_edited", {
				location_id: store.cashInLocation.locationId,
				from_review: true,
			} as Record<string, unknown>);

			// Clear from_review and go back to review
			const searchWithoutFromReview = { ...search };
			delete searchWithoutFromReview.from_review;
			continueToNextPage(searchWithoutFromReview);
			return;
		}

		// Build the quote request with stagingInfo for deposit flow
		const quoteRequest: QuoteRequest = {
			targetAudience: "CONSUMER_FACING",
			userLanguage: "en",
			destinationCountryCode: store.payoutCountry || "USA",
			destinationCountrySubdivisionCode: store.state
				? `${store.payoutCountry}-${store.state}`
				: undefined,
			payoutAmount: {
				value: Number(store.amount) || 0,
				currencyCode: "USDC",
			},
			payoutOptionCode: "DIRECT_TO_ACCT",
			sendCurrencyCode: "USD",
			sendAmount: {
				value: Number(store.amount) || 0,
				currencyCode: "USDC",
			},
			payoutCurrencyCode: "USD",
			// Include stagingInfo with location details for cash-in
			stagingInfo: {
				stageTransaction: true,
				sendLocationAgentPartnerId: store.cashInLocation.locationId,
				formFreeCompletion: store.cashInLocation.formFreeCompletion,
			},
		};

		trackEvent("deposit_quote_requested", {
			location_id: store.cashInLocation.locationId,
			amount: store.amount,
			country: store.payoutCountry,
		} as Record<string, unknown>);

		// Call quote API
		quoteMutation.mutate(quoteRequest);
	};

	// Format distance for display
	const formatDistance = (distance?: number) => {
		if (!distance) return "";
		// Convert miles to meters for display (matching design)
		const meters = Math.round(distance * 1609.34);
		if (meters >= 1000) {
			return `${(meters / 1000).toFixed(1)} km`;
		}
		return `${meters} meters`;
	};

	// Get location hours display
	const getHoursDisplay = (location: PickupLocation) => {
		const is24Hours = isLocation24Hours(location.hours);
		const isOpen = isLocationOpen(location.hours);
		const closingTime = getClosingTime(location.hours);

		if (is24Hours) return t`Open 24 hours`;
		if (isOpen && closingTime) return t`Open until ${closingTime}`;
		return t`Closed`;
	};

	// Loading state
	if (geoLoading) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen p-8">
				<LoadingSpinner size="lg" />
				<RampsText variant="body-sm" color="secondary" className="mt-4">
					{t`Getting your location...`}
				</RampsText>
			</div>
		);
	}

	return (
		<div className="flex flex-col min-h-screen max-w-screen-sm mx-auto bg-ramps-background-base">
			{/* Header */}
			<div className="px-4 pt-6 pb-4">
				<RampsText
					variant="title-lg"
					color="primary"
					className="font-semibold mb-4"
				>
					{t`Choose deposit location`}
				</RampsText>

				{/* Search bar with toggle */}
				<div className="flex items-center gap-3">
					{/* Search Input */}
					<div className="flex-1 relative">
						<Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ramps-text-secondary" />
						<input
							type="text"
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							placeholder={t`Search`}
							className="w-full pl-12 pr-10 py-3 rounded-full bg-neutral-100 text-ramps-text-primary placeholder:text-ramps-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500"
						/>
						{inputValue && (
							<button
								type="button"
								onClick={() => setInputValue("")}
								className="absolute right-4 top-1/2 -translate-y-1/2"
							>
								<X className="w-5 h-5 text-ramps-text-secondary" />
							</button>
						)}
					</div>

					{/* View Toggle Button */}
					<button
						type="button"
						onClick={() => setViewMode(viewMode === "map" ? "list" : "map")}
						className="w-12 h-12 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors"
						aria-label={
							viewMode === "map"
								? t`Switch to list view`
								: t`Switch to map view`
						}
					>
						{viewMode === "map" ? (
							<List className="w-6 h-6 text-ramps-text-primary" />
						) : (
							<MapIcon className="w-6 h-6 text-ramps-text-primary" />
						)}
					</button>
				</div>

				{/* Autocomplete suggestions dropdown */}
				{inputValue.length >= 2 && (
					<div className="mt-2 relative z-50">
						{isAutocompleteLoading && (
							<div className="px-4 py-3 bg-white rounded-lg shadow-lg border border-neutral-200">
								<RampsText variant="body-sm" color="secondary">
									{t`Searching...`}
								</RampsText>
							</div>
						)}
						{!isAutocompleteLoading && suggestions.length > 0 && (
							<div className="bg-white rounded-lg shadow-lg border border-neutral-200 divide-y divide-neutral-100 overflow-hidden">
								{suggestions.map((suggestion) => (
									<button
										key={suggestion.place_id}
										type="button"
										onClick={() => handlePlaceSelect(suggestion)}
										className="w-full px-4 py-3 text-left hover:bg-neutral-50 transition-colors flex items-center gap-3"
									>
										<MapPin className="w-5 h-5 text-ramps-text-secondary shrink-0" />
										<div className="flex-1 min-w-0">
											<RampsText variant="body-default" color="primary">
												{suggestion.structured_formatting?.main_text ||
													suggestion.description}
											</RampsText>
											{suggestion.structured_formatting?.secondary_text && (
												<RampsText
													variant="body-sm"
													color="secondary"
													className="truncate"
												>
													{suggestion.structured_formatting.secondary_text}
												</RampsText>
											)}
										</div>
									</button>
								))}
							</div>
						)}
					</div>
				)}
			</div>

			{/* Main Content Area */}
			{isLoading ? (
				<div className="flex flex-col items-center justify-center flex-1 p-8">
					<LoadingSpinner size="lg" />
					<RampsText variant="body-sm" color="secondary" className="mt-4">
						{t`Finding locations...`}
					</RampsText>
				</div>
			) : locationsError ? (
				<div className="flex flex-col items-center justify-center flex-1 p-8">
					<MapPin className="w-16 h-16 text-neutral-300 mb-4" />
					<RampsText
						variant="body-default"
						color="secondary"
						className="text-center mb-2"
					>
						{t`Unable to find locations`}
					</RampsText>
					<RampsText
						variant="body-sm"
						color="secondary"
						className="text-center mb-4"
					>
						{t`Search for a city or address in ${store.payoutCountry || countryCode} to find nearby locations`}
					</RampsText>
					<RampsButton onClick={() => refetch()} variant="secondary">
						{t`Retry`}
					</RampsButton>
				</div>
			) : depositLocations.length === 0 ? (
				<div className="flex flex-col items-center justify-center flex-1 p-8">
					<MapPin className="w-16 h-16 text-neutral-300 mb-4" />
					<RampsText
						variant="body-default"
						color="secondary"
						className="text-center mb-2"
					>
						{t`No cash-in locations found`}
					</RampsText>
					<RampsText
						variant="body-sm"
						color="secondary"
						className="text-center mb-4"
					>
						{t`Try searching for a different area`}
					</RampsText>
					<RampsButton onClick={() => refetch()} variant="secondary">
						{t`Retry`}
					</RampsButton>
				</div>
			) : viewMode === "map" ? (
				/* Map View */
				<div className="flex-1 flex flex-col relative min-h-0">
					{/* Map - using absolute positioning to fill container */}
					<div className="flex-1 relative min-h-[300px]">
						<div className="absolute inset-0">
							<MapComponent
								locations={depositLocations}
								selectedLocationId={selectedLocationId}
								center={{
									lat: searchCoordinates.lat,
									lng: searchCoordinates.lng,
								}}
								className="w-full h-full"
								height="100%"
								onMarkerClick={handleLocationSelect}
							/>
						</div>

						{/* Search this area button */}
						<button
							type="button"
							onClick={handleSearchThisArea}
							className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 bg-white rounded-full shadow-md text-sm font-medium text-ramps-text-primary hover:bg-neutral-50 transition-colors"
						>
							{t`Search this area`}
						</button>

						{/* GPS button - positioned below Google Maps controls */}
						<button
							type="button"
							onClick={handleCenterOnUser}
							className="absolute top-16 right-3 z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-neutral-50 transition-colors"
							aria-label={t`Center on my location`}
						>
							<Navigation className="w-5 h-5 text-ramps-text-primary" />
						</button>

						{/* Floating Selected Location Card */}
						{selectedLocation && (
							<div className="absolute bottom-4 left-4 right-4 z-10 bg-white rounded-2xl shadow-lg p-4">
								<div className="flex items-start justify-between mb-2">
									<RampsText
										variant="body-bold"
										color="primary"
										className="flex-1"
									>
										{selectedLocation.name}
									</RampsText>
									<RampsText variant="body-sm" color="secondary">
										{formatDistance(selectedLocation.distance)}
									</RampsText>
								</div>

								<div className="flex items-center gap-2 mb-2">
									<MapPin className="w-4 h-4 text-ramps-text-secondary shrink-0" />
									<RampsText variant="body-sm" color="secondary">
										{selectedLocation.address}, {selectedLocation.city},{" "}
										{selectedLocation.state} {selectedLocation.zipCode}
									</RampsText>
								</div>

								<div className="flex items-center gap-2 mb-4">
									<Clock className="w-4 h-4 text-ramps-text-secondary shrink-0" />
									<RampsText variant="body-sm" color="secondary">
										{getHoursDisplay(selectedLocation)}
									</RampsText>
								</div>

								<RampsButton
									onClick={handleContinue}
									loading={quoteMutation.isPending}
									disabled={quoteMutation.isPending}
									className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800"
								>
									{t`Continue`}
								</RampsButton>
							</div>
						)}
					</div>
				</div>
			) : (
				/* List View */
				<div className="flex-1 flex flex-col">
					{/* Locations List */}
					<div className="flex-1 overflow-y-auto">
						{depositLocations.map((location) => {
							const isSelected = selectedLocationId === location.id;

							return (
								<button
									key={location.id}
									type="button"
									onClick={() => handleLocationSelect(location.id)}
									className={`w-full flex items-start gap-3 p-4 border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${
										isSelected ? "bg-neutral-50" : ""
									}`}
								>
									{/* Brand Icon Placeholder */}
									<div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
										<MapPin className="w-5 h-5 text-blue-600" />
									</div>

									{/* Location Info */}
									<div className="flex-1 min-w-0 text-left">
										<div className="flex items-start justify-between gap-2">
											<RampsText
												variant="body-bold"
												color="primary"
												className="truncate"
											>
												{location.name}
											</RampsText>
											<RampsText
												variant="body-sm"
												color="secondary"
												className="shrink-0"
											>
												{formatDistance(location.distance)}
											</RampsText>
										</div>

										<RampsText
											variant="body-sm"
											color="secondary"
											className="mt-1"
										>
											{location.address}
										</RampsText>
										<RampsText variant="body-sm" color="secondary">
											{location.city}, {location.state} {location.zipCode}
										</RampsText>
										{location.phone && (
											<RampsText variant="body-sm" color="secondary">
												{location.phone}
											</RampsText>
										)}

										<div className="flex items-center justify-between mt-1">
											<RampsText variant="body-sm" color="secondary">
												{getHoursDisplay(location)}
											</RampsText>
										</div>
									</div>

									{/* Checkmark Indicator */}
									{isSelected && (
										<div className="shrink-0 mt-1 w-6 h-6 rounded-full bg-black flex items-center justify-center">
											<Check className="w-4 h-4 text-white" />
										</div>
									)}
								</button>
							);
						})}
					</div>

					{/* Bottom Action */}
					<div className="p-4 bg-white border-t border-neutral-100">
						<RampsButton
							onClick={selectedLocation ? handleContinue : undefined}
							disabled={!selectedLocation || quoteMutation.isPending}
							loading={quoteMutation.isPending}
							className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{t`Continue`}
						</RampsButton>
					</div>
				</div>
			)}
		</div>
	);
}

export const Route = createFileRoute("/ramps/deposit-location")({
	validateSearch: (search) => validateRampsSearch(search),
	component: DepositLocationPage,
});
